import React, { useState } from 'react';
import { LogoIcon } from './icons';
import { functions, auth } from '../services/firebase';
import { getStripe, STRIPE_PRICE_IDS } from '../services/stripe';
import PricingView from './PricingView';

interface SubscriptionWallProps {
    status: 'trial_ended' | 'canceled' | 'past_due';
}

const SubscriptionWall: React.FC<SubscriptionWallProps> = ({ status }) => {
    const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleSubscribe = async (priceId: string) => {
        if (!functions) {
            setError("El servicio de pago no está disponible en este momento.");
            return;
        }
        setLoadingPlan(priceId);
        setError(null);

        try {
            const createCheckout = functions.httpsCallable('createStripeCheckout');
            const result = await createCheckout({
                priceId: priceId,
                successUrl: `${window.location.origin}?subscription_success=true`,
                cancelUrl: window.location.href,
            });
            
            const sessionId = (result.data as any).sessionId;
            if (!sessionId) {
                 throw new Error("No se pudo obtener el ID de la sesión de pago.");
            }

            const stripe = await getStripe();
            if (stripe) {
                await (stripe as any).redirectToCheckout({ sessionId });
            } else {
                 throw new Error("No se pudo cargar la librería de Stripe.");
            }

        } catch (err: any) {
            console.error("Error creating checkout session:", err);
            // The backend now sends a user-friendly message in err.details.message
            const userFriendlyError = err.details?.message ||
              "Ocurrió un error inesperado al conectar con el servicio de pagos. Por favor, inténtalo de nuevo.";
            setError(userFriendlyError);
            setLoadingPlan(null);
        }
    };

    const handleLogout = async () => {
        if (auth) {
            await auth.signOut();
        }
    };

    const messages = {
        trial_ended: {
            title: 'Un plan para cada necesidad',
            message: 'Tu prueba gratuita ha terminado. ¡No pierdas tu progreso! Elige el plan que mejor se adapte a ti para seguir tomando el control de tus finanzas.',
        },
        canceled: {
            title: 'Reactiva tu suscripción',
            message: 'Tu suscripción fue cancelada. Si quieres volver a acceder a tus datos, elige un plan para reactivarla.',
        },
        past_due: {
            title: 'Actualiza tu método de pago',
            message: 'No hemos podido procesar tu último pago. Por favor, elige un plan para actualizar tu información de facturación y restaurar el acceso completo.',
        }
    };

    const content = messages[status];

    return (
        <div className="flex items-center justify-center min-h-screen bg-light-background dark:bg-background p-4">
            <div className="w-full max-w-5xl bg-light-surface dark:bg-surface rounded-xl shadow-2xl border border-light-border dark:border-border p-8 text-center">
                <LogoIcon className="w-16 h-16 text-primary mx-auto mb-4" />
                <h1 className="text-3xl md:text-4xl font-bold text-light-text-primary dark:text-text-primary">{content.title}</h1>
                <p className="mt-4 max-w-2xl mx-auto text-light-text-secondary dark:text-text-secondary">{content.message}</p>
                
                <div className="mt-8">
                    <PricingView onSelectPlan={handleSubscribe} loadingPlan={loadingPlan} />
                </div>
                
                {error && <p className="mt-6 text-sm text-red-500">{error}</p>}
                
                <div className="mt-8">
                    <button 
                        onClick={handleLogout}
                        className="text-sm font-medium text-light-text-secondary dark:text-text-secondary hover:underline"
                    >
                        Cerrar Sesión
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionWall;